import type { FC } from "react";
import { useEffect, useState } from "react";

import { sendTelemetryEvent } from "@/lib/telemetry";
import { AvailabilityState } from "@/src/cashback/CashbackCommonInterfaces";
import type { IScrollPaginationResult } from "@/src/cashback/MainPage/ScrollPagination";
import { languageSelector, useUserAuthenticationContext, useViewportInfoContext } from "@/src/contexts";
import TelemetryButtonV2 from "@Components/Button/TelemetryButton/TelemetryButtonV2";
import useAjax from "@Components/Hooks/useAjax";
import { Loading, LoadingAppearance } from "@Components/Loading";
import { Modal } from "@Components/Modal";
import { CommonEventType, MgmEventType, TelemetryEntity, urlFormat } from "@Components/Modules/Helpers";
import Text from "@Components/Text";
import { Default as MgmPolecaj } from "@Routes/MemberGetMemberLinks";
import * as stylex from "@stylexjs/stylex";

import { type QuerySmMax, colors } from "../../../styles/tokens.stylex";
import type { IMgmSummaryValues } from "../MainPage/MgmMainPage.d";
import { MgmSharedResources } from "./MgmShared.Resources";
import { MgmSharedResourcesEn } from "./MgmShared.Resources.En";

import * as css from "./MgmTransactionsSummary.module.css";

const smMax: QuerySmMax = "@media (max-width: 989px)";

const styles = stylex.create({
    tabsHeader: {
        gap: { default: "40px", [smMax]: "20px" },
        display: "inline-flex",
        flexDirection: "row",
    },

    headerStatus: {
        margin: 0,
        color: colors.grayLight,

        position: "relative",
        paddingBottom: "5px",
        paddingLeft: "8px",
        paddingRight: "8px",
        paddingTop: "10px",
    },

    headerActive: {
        color: colors.b_BrandMagenta,
        borderBottomColor: colors.b_BrandMagenta,
        borderBottomStyle: "solid",
        borderBottomWidth: "2px",
    },

    headerBtn: {
        padding: 0,
    },
});

export interface IMgmTransactionItem {
    id: string;
    brandName?: string;
    orderId?: string;
    cashbackAmount?: string;
    transactionCreateDate?: string;
    transactionCreateDateString?: string;
}

interface IMgmTransactionsSummaryModalData extends IMgmSummaryValues {
    transactionList: IScrollPaginationResult<IMgmTransactionItem>;
}

interface TabInfo {
    text: string;
    telemetryInfo: string;
}

export interface IMgmTransactionsSummaryModal {
    onClose?: () => void;
}

const MgmTransactionsSummaryModal: FC<IMgmTransactionsSummaryModal> = (props) => {
    const [selectedTab, setSelectedTab] = useState(AvailabilityState.Pending);
    const {
        mgmTransactionSummary: resourcesSummary,
        noApprovedBonus,
        noPendingBonus,
        noRejectedBonus,
    } = languageSelector(MgmSharedResources, MgmSharedResourcesEn);

    const { isMobile } = useViewportInfoContext();
    const { isAuthenticated } = useUserAuthenticationContext();

    useEffect(
        () =>
            sendTelemetryEvent({
                eventName: `${TelemetryEntity.Cashback}-${MgmEventType.MgmTrxModal}`,
                actionType: "View",
            }),
        [],
    );

    const dataUrl = urlFormat(
        MgmPolecaj,
        {
            action: "GetUserMgmTransactions",
            transactionStatus: "{transactionStatus}",
            pageIndex: "{pageIndex}",
            pageSize: "{pageSize}",
        },
        true,
    );

    const {
        request,
        data: result,
        isLoading,
    } = useAjax<IMgmTransactionsSummaryModalData>({
        ajaxAction: dataUrl,
    });

    const tabList = new Map<AvailabilityState, TabInfo>();
    tabList.set(AvailabilityState.Pending, {
        text: resourcesSummary.tabPending,
        telemetryInfo: MgmEventType.MgmTrxModalPendingTab,
    });
    tabList.set(AvailabilityState.Approved, {
        text: resourcesSummary.tabApproved,
        telemetryInfo: MgmEventType.MgmTrxModalApprovedTab,
    });
    tabList.set(AvailabilityState.Rejected, {
        text: resourcesSummary.tabRejected,
        telemetryInfo: MgmEventType.MgmTrxModalRejectedTab,
    });

    useEffect(() => {
        if (isAuthenticated) {
            request({
                transactionStatus: selectedTab,
                pageIndex: "0",
                pageSize: "1000",
            });
        }
    }, [selectedTab]);

    const getEmptyTrx = () => {
        const { label, text } =
            selectedTab === AvailabilityState.Approved
                ? noApprovedBonus
                : selectedTab === AvailabilityState.Pending
                  ? noPendingBonus
                  : selectedTab === AvailabilityState.Rejected
                    ? noRejectedBonus
                    : undefined;

        return (
            text &&
            label && (
                <div className={css.emptyTrx}>
                    <p>{label}</p>
                    <p>{text}</p>
                </div>
            )
        );
    };

    const renderTransactionList = () => {
        if (isLoading) {
            return <Loading appearance={LoadingAppearance.Small} />;
        }

        if (result?.transactionList?.emptyList) {
            return getEmptyTrx();
        }

        return (
            <div className={css.transactionList}>
                <ul>
                    <li className={css.trxListHeader}>
                        <span>
                            {isMobile
                                ? resourcesSummary.tableHeaderStatusLabel
                                : resourcesSummary.tableHeaderDateLabel}
                        </span>
                        <span>{resourcesSummary.tableHeaderAmountLabel}</span>
                    </li>
                    {result?.transactionList?.data.map((trx) => (
                        <li key={trx.id}>
                            <span className={css.trxName}>{trx.brandName}</span>
                            <span className={css.trxOrderId}>{trx.orderId}</span>
                            <span className={css.trxDate}>
                                {`${isMobile ? resourcesSummary.tableHeaderDateLabel : ""} ${trx.transactionCreateDateString}`}
                            </span>
                            <span className={css.trxAmount}>{trx.cashbackAmount}</span>
                        </li>
                    ))}
                </ul>
            </div>
        );
    };

    const getLink = (tab: AvailabilityState): React.ReactNode => {
        if (!tabList.has(tab)) {
            return null;
        }

        const tabDetails = tabList.get(tab);

        return (
            <TelemetryButtonV2
                key={tab}
                variant="transparentMagenta"
                styleX={styles.headerBtn}
                onClick={() => setSelectedTab(tab)}
                telemetryCustom={{
                    eventName: `${TelemetryEntity.Cashback}-${tabDetails.telemetryInfo}`,
                    actionType: "Click",
                }}
            >
                <Text
                    textElement="h3"
                    styleX={[styles.headerStatus, tab === selectedTab && styles.headerActive]}
                    dangerouslySetInnerHTML={{ __html: tabDetails.text }}
                />
            </TelemetryButtonV2>
        );
    };

    return (
        <Modal
            className={css.mgmTransactionsSummaryComponent}
            onClose={props.onClose}
            closeCustomTelemetry={{
                eventName: `${TelemetryEntity.Common}-${CommonEventType.exit}`,
                actionType: "Click",
            }}
            allowBodyScroll={false}
            isOpen
            closeOnBlur
            closeOnEsc
        >
            <div className={css.header}>
                <p className={css.title}>{resourcesSummary.modalTitle}</p>
                <div {...stylex.props(styles.tabsHeader)}>{[...tabList].map((x) => getLink(x[0]))}</div>
            </div>
            <div className={css.transaction}>{renderTransactionList()}</div>
            <div className={css.summary}>
                <div className={css.cashbackValues}>
                    <div className={css.cashbackValuesWrapper}>
                        <p className={css.registeredUsersNumber}>{result?.mgmReceiverCount}</p>
                        <p className={css.registeredUsersToMeLabel}>
                            {resourcesSummary.usersRegisteredToMeLabel}
                        </p>
                        <p className={css.customValues}>
                            {resourcesSummary.completedBonusLabel}
                            <b>{result?.mgmApprovedWithdrawnBonusCount}</b>
                        </p>
                        <p className={css.customValues}>
                            {resourcesSummary.beforeCompletedBonusLabel}
                            <b>{result?.mgmPendingBonusCount}</b>
                        </p>
                    </div>
                    <div className={css.totalWithdrawalAmount}>
                        <p>
                            {resourcesSummary.totalBonusReceivedLabel.replace(
                                "{totalAmount}",
                                `${result?.mgmWithdrawnBonusSum}`,
                            )}
                        </p>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default MgmTransactionsSummaryModal;
